import JSZip from "jszip";
import JSZipUtils from "jszip-utils";
import { saveAs } from "file-saver";

import IdealStudio from "../editor/main";
import Plugins from "./plugins";
import { ID_PREFIX_SECTION } from "../../common/constants";
import { buildSceneState, sceneIds, sceneName } from "./sceneScormState";
import { escapeRegExp, assetKey, credentialsFor } from "../../common/utils";
import {
  collectTrainingFonts,
  generateGoogleFontsLink,
  generateFontFaceCSS,
  fetchGoogleFontsFiles,
  buildExportFontMap,
} from "../../common/font_extractor.es6";

const visor_template = require("../../dist/lib/visor/index.ejs");
let getDistinctName = function (name, namesUsed) {
  namesUsed[name] = namesUsed[name] + 1;
  return name + namesUsed[name];
};

let titleModifier = function (name) {
  if (name.indexOf("\:") !== -1) {
    name = name.split("\:")[0];
  }
  return name;
};

// Allowed media file extensions (whitelist)
const allowedExtensions = [
  // Images
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".svg",
  ".webp",
  ".bmp",
  ".ico",
  // Videos
  ".mp4",
  ".mov",
  ".avi",
  ".webm",
  ".mkv",
  ".flv",
  ".wmv",
  ".m4v",
  // Audio
  ".mp3",
  ".wav",
  ".ogg",
  ".m4a",
  ".aac",
  ".flac",
  ".wma",
  // Documents
  ".pdf",
];

// Helper function to filter media files (images, videos, audio) from filesUploaded
// Also includes external URLs (http/https) for self-contained exports
let filterUserContentFiles = function (filesUploaded) {
  if (!filesUploaded || typeof filesUploaded !== "object") {
    return {};
  }

  const filtered = {};

  for (let key in filesUploaded) {
    let file = filesUploaded[key];
    if (
      !file ||
      !file.name ||
      typeof file.name !== "string" ||
      !file.url ||
      typeof file.url !== "string"
    ) {
      continue;
    }

    // Include external URLs (http/https) for self-contained exports
    const isExternalUrl = file.url.startsWith("http://") || file.url.startsWith("https://");

    const urlWithoutQuery = file.url.split("?")[0].toLowerCase();
    const isMediaFile = allowedExtensions.some((ext) => urlWithoutQuery.endsWith(ext));

    if (isMediaFile || isExternalUrl) {
      filtered[key] = file;
    }
  }

  return filtered;
};

// State keys deliberately excluded from the export content scope
const EXPORT_SCOPE_IGNORED_KEYS = new Set([
  // editor/UI state
  "version",
  "lastActionDispatched",
  "boxSelectedAll",
  "boxSelected",
  "boxLevelSelected",
  "indexSelected",
  "navItemsIds",
  "navItemSelected",
  "containedViewSelected",
  "displayMode",
  "isBusy",
  // author-side data
  "uploadedTemplates",
  "userTemplates",
  // handled separately by the export
  "filesUploaded",
  "status",
  "subtitleBundle",
  "everPublished",
  "fromScorm",
  "export",
  "id",
  "platform",
]);

// Single definition of "media used in the training" — shared by
// filterUnusedFilesUploaded and extractExternalUrlsFromState.
// Orphaned sounds/animations of deleted scenes/boxes are pruned.
let buildExportContentScope = function (state) {
  if (!state || typeof state !== "object") {
    return {};
  }

  const pruneByOwner = (byId, owners) => {
    if (!byId || typeof byId !== "object") return byId;
    const pruned = {};
    for (let key in byId) {
      if (owners.some((owner) => owner && owner[key])) {
        pruned[key] = byId[key];
      }
    }
    return pruned;
  };

  const scope = {
    globalConfig: state.globalConfig,
    navItemsById: state.navItemsById,
    containedViewsById: state.containedViewsById,
    boxesById: state.boxesById,
    pluginToolbarsById: state.pluginToolbarsById,
    viewToolbarsById: state.viewToolbarsById,
    marksById: state.marksById,
    exercises: state.exercises,
    // keyed by scene id
    soundList:
      state.navItemsById && state.containedViewsById
        ? pruneByOwner(state.soundList, [state.navItemsById, state.containedViewsById])
        : state.soundList,
    // keyed by box id
    animationsById: state.boxesById
      ? pruneByOwner(state.animationsById, [state.boxesById])
      : state.animationsById,
  };

  if (process.env.NODE_ENV !== "production") {
    for (let key in state) {
      if (!(key in scope) && !EXPORT_SCOPE_IGNORED_KEYS.has(key)) {
        console.warn(
          `buildExportContentScope: unknown state key "${key}" — add it to the scope or EXPORT_SCOPE_IGNORED_KEYS`
        );
      }
    }
  }

  return scope;
};

// Extracts external media URLs used in the training content
let extractExternalUrlsFromState = function (state, existingUrls = new Set()) {
  const foundUrls = new Map();

  const isMediaUrl = (url) => {
    if (!url || typeof url !== "string") return false;
    if (!url.startsWith("http://") && !url.startsWith("https://")) return false;
    if (url.includes("base64")) return false;
    const urlWithoutQuery = url.split("?")[0].toLowerCase();
    return allowedExtensions.some((ext) => urlWithoutQuery.endsWith(ext));
  };

  const getFileInfoFromUrl = (url) => {
    const urlWithoutQuery = url.split("?")[0];
    const filename = urlWithoutQuery.split("/").pop() || "unknown";
    const extMatch = filename.match(/\.([^.]+)$/);
    const extension = extMatch ? extMatch[0].toLowerCase() : "";

    let mimetype = "application/octet-stream";
    if ([".jpg", ".jpeg"].includes(extension)) mimetype = "image/jpeg";
    else if (extension === ".png") mimetype = "image/png";
    else if (extension === ".gif") mimetype = "image/gif";
    else if (extension === ".svg") mimetype = "image/svg+xml";
    else if (extension === ".webp") mimetype = "image/webp";
    else if (extension === ".bmp") mimetype = "image/bmp";
    else if (extension === ".ico") mimetype = "image/x-icon";
    else if (extension === ".mp4") mimetype = "video/mp4";
    else if (extension === ".webm") mimetype = "video/webm";
    else if (extension === ".mp3") mimetype = "audio/mpeg";
    else if (extension === ".wav") mimetype = "audio/wav";
    else if (extension === ".ogg") mimetype = "audio/ogg";
    else if (extension === ".m4a") mimetype = "audio/mp4";
    else if (extension === ".pdf") mimetype = "application/pdf";

    return { name: filename, mimetype, extension };
  };

  const scanObject = (obj, path = "") => {
    if (!obj) return;

    if (typeof obj === "string") {
      if (isMediaUrl(obj) && !existingUrls.has(obj)) {
        const fileInfo = getFileInfoFromUrl(obj);
        foundUrls.set(obj, { url: obj, ...fileInfo });
      }
      const urlPatterns = [
        /https?:\/\/[^\s"'<>\\]+\.(jpg|jpeg|png|gif|svg|webp|bmp|ico|mp4|mov|avi|webm|mkv|flv|wmv|m4v|mp3|wav|ogg|m4a|aac|flac|wma|pdf)(?:\?[^\s"'<>\\]*)?/gi,
        /https%3A%2F%2F[^\s"'<>\\%]+\.(jpg|jpeg|png|gif|svg|webp|bmp|ico|mp4|mov|avi|webm|mkv|flv|wmv|m4v|mp3|wav|ogg|m4a|aac|flac|wma|pdf)(?:%3F[^\s"'<>\\%]*)?/gi,
      ];

      for (const pattern of urlPatterns) {
        let match;
        while ((match = pattern.exec(obj)) !== null) {
          let url = match[0];
          if (url.includes("%3A%2F%2F")) {
            try {
              url = decodeURIComponent(url);
            } catch (e) {}
          }
          if (isMediaUrl(url) && !existingUrls.has(url) && !foundUrls.has(url)) {
            const fileInfo = getFileInfoFromUrl(url);
            foundUrls.set(url, { url, ...fileInfo });
          }
        }
      }
      return;
    }

    if (Array.isArray(obj)) {
      obj.forEach((item, index) => scanObject(item, `${path}[${index}]`));
      return;
    }

    if (typeof obj === "object") {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          scanObject(obj[key], `${path}.${key}`);
        }
      }
    }
  };

  const scope = buildExportContentScope(state);
  for (let key in scope) {
    if (scope[key]) {
      scanObject(scope[key], key);
    }
  }

  // Motion/3D scene documents (state.htmlUrl) are .html, not media, yet the gated
  // /api/f/ URL answers 401 once the package leaves the app — pack them too.
  Object.values(scope.pluginToolbarsById || {}).forEach((toolbar) => {
    const url = toolbar && toolbar.state && toolbar.state.htmlUrl;
    if (
      typeof url === "string" &&
      /^https?:\/\/[^?#]+\/api\/f\/[^?#]+\.html(?:[?#]|$)/i.test(url) &&
      !existingUrls.has(url)
    ) {
      foundUrls.set(url, { url, name: url.split("?")[0].split("/").pop(), mimetype: "text/html" });
    }
  });

  return foundUrls;
};

let mergeExternalUrls = function (filesUploaded, extractedUrls) {
  const merged = { ...filesUploaded };
  let counter = Date.now();

  extractedUrls.forEach((fileInfo, url) => {
    const alreadyExists = Object.values(merged).some((f) => f && assetKey(f.url) === assetKey(url));
    if (!alreadyExists) {
      const id = `file-extracted-${counter++}`;
      merged[id] = {
        id,
        url: fileInfo.url,
        name: fileInfo.name,
        mimetype: fileInfo.mimetype,
        keywords: null,
      };
    }
  });

  return merged;
};

let parseEJS = function (path, page, state, fromScorm, selfContained, fontMap = {}) {
  state.fromScorm = fromScorm;

  const usedFonts = collectTrainingFonts(state);
  const googleFontsLink = generateGoogleFontsLink(usedFonts);
  // Only families whose files are in the zip. A rule pointing at a missing
  // file comes after the Google link in the template and shadows it, and for
  // a non-Google family it would shadow the installed system font too.
  const bundledFonts = new Map([...usedFonts].filter(([name]) => fontMap[name]));
  const dynamicFontFaceCSS = generateFontFaceCSS(bundledFonts, fontMap);

  if (page !== 0 && state.navItemsById[page]) {
    if (Object.keys(state.navItemsById[page].extraFiles).length !== 0) {
      let extraFileBox = Object.keys(state.navItemsById[state.navItemSelected].extraFiles)[0];
      let extraFileContainer = state.pluginToolbarsById[extraFileBox];
      let bundlePath =
        fromScorm && !selfContained
          ? IdealStudio.Config.visor_bundle_scorm
          : IdealStudio.Config.visor_bundle_zip || IdealStudio.Config.visor_bundle;
      // Replace versioned bundle name with generic one for SCORM packages and HTML exports
      if (fromScorm || (!fromScorm && !selfContained)) {
        bundlePath = bundlePath.replace("visor-bundle-200-1.js", "visor-bundle.js");
      }
      return visor_template({
        visor_bundle_path: bundlePath,
        state: state,
        reason: fromScorm ? "scorm" : "html",
        googleFontsLink: selfContained ? "" : googleFontsLink,
        dynamicFontFaceCSS: dynamicFontFaceCSS,
      });
    }
  }

  state.fromScorm = fromScorm;
  let bundlePath =
    fromScorm && !selfContained
      ? IdealStudio.Config.visor_bundle_scorm
      : IdealStudio.Config.visor_bundle_zip || IdealStudio.Config.visor_bundle;
  // Replace versioned bundle name with generic one for SCORM packages and HTML exports
  if (fromScorm || (!fromScorm && !selfContained)) {
    bundlePath = bundlePath.replace("visor-bundle-200-1.js", "visor-bundle.js");
  }
  return visor_template({
    visor_bundle_path: bundlePath,
    state: state,
    reason: fromScorm ? "scorm" : "html",
    googleFontsLink: selfContained ? "" : googleFontsLink,
    dynamicFontFaceCSS: dynamicFontFaceCSS,
  });
};

// Base64 payloads can't contain "://", so no asset URL lives inside them —
// skipping them keeps the export scans off inline images (crops are stored this way).
const BASE64_DATA_URL = /data:[\w.+/-]+;base64,[A-Za-z0-9+/=]+/g;

// Self-contained export: names each file's zip path and swaps its URLs in
// strState in a single pass. One replace per file re-scanned the whole state
// (minutes on large trainings). Alternatives keep file order, so a URL that
// prefixes another resolves as it did with the sequential replaces.
let localizeFileUrls = function (strState, files) {
  const usedNames = [];
  const filesUploaded = [];
  const pathByUrl = new Map();
  let index = 0;
  for (let f in files) {
    const file = files[f];
    if (file.url && file.url.includes("base64")) {
      continue;
    }

    let randomFileName = Math.random().toString().substring(2);
    let extension = "";
    if (file.url && typeof file.url === "string") {
      const urlWithoutQuery = file.url.split("?")[0];
      const urlFilename = urlWithoutQuery.split("/").pop();
      const extMatch = urlFilename.match(/\.([^.]+)$/);
      if (extMatch) {
        extension = extMatch[0];
      }
    }

    let name = "idealStudio_" + index + "_" + randomFileName + (extension || "");
    if (usedNames.indexOf(name) > -1) {
      name = Date.now() + "_" + name;
    }
    usedNames.push(name);
    filesUploaded.push(file);
    const isPdf = name.toLowerCase().endsWith(".pdf");
    const replacePath = isPdf ? "images/" + name : "images/uploadedFiles/" + name;
    if (file.url && !pathByUrl.has(file.url)) {
      pathByUrl.set(file.url, replacePath);
    }
    index++;
  }

  if (pathByUrl.size > 0) {
    const r = new RegExp(Array.from(pathByUrl.keys(), escapeRegExp).join("|"), "g");
    strState = strState.replace(r, (url) => pathByUrl.get(url));
  }
  return { strState, usedNames, filesUploaded };
};

let filterUnusedFilesUploaded = function (state) {
  if (!state || !state.filesUploaded || Object.keys(state.filesUploaded).length === 0) {
    return state ? state.filesUploaded || {} : {};
  }

  const stateToCheck = buildExportContentScope(state);

  let fullState = "";
  try {
    fullState = JSON.stringify(stateToCheck);
  } catch (e) {
    return state.filesUploaded;
  }
  // Searched without base64 payloads; inline (data:) files are still matched on the full state.
  const strState = fullState.replace(BASE64_DATA_URL, "");

  const usedFiles = {};
  for (const key in state.filesUploaded) {
    const file = state.filesUploaded[key];
    if (!file || !file.url) {
      continue;
    }

    if (file.url.startsWith("data:")) {
      if (fullState.includes(file.url)) {
        usedFiles[key] = file;
      }
    } else if (strState.includes(file.url)) {
      usedFiles[key] = file;
    } else {
      let encodedUrl = "";
      try {
        encodedUrl = encodeURI(file.url);
      } catch (e) {}

      let decodedUrl = "";
      try {
        decodedUrl = decodeURI(file.url);
      } catch (e) {}

      if (encodedUrl && encodedUrl !== file.url && strState.includes(encodedUrl)) {
        usedFiles[key] = file;
      } else if (decodedUrl && decodedUrl !== file.url && strState.includes(decodedUrl)) {
        usedFiles[key] = file;
      } else {
        const namePart = file.url.split("/").pop().split("?")[0];
        if (namePart && namePart.length > 4 && strState.includes(namePart)) {
          usedFiles[key] = file;
        } else {
          console.log("Filtering out unused file from export:", file.url);
        }
      }
    }
  }
  return usedFiles;
};

const fetchWithRetry = async (url, options, retries = 3) => {
  const delays = [1000, 3000, 5000];
  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response;
    } catch (err) {
      if (err.name === "AbortError" || (options && options.signal && options.signal.aborted)) {
        throw err;
      }
      if (i === retries - 1) throw err;
      console.warn(`[Retry ${i + 1}/${retries}] Retrying fetch for ${url} after ${delays[i]}ms`);
      await new Promise((resolve) => setTimeout(resolve, delays[i] || 3000));
    }
  }
};

const getBinaryContentWithRetry = (url, retries = 3) => {
  const delays = [1000, 3000, 5000];
  return new Promise((resolve, reject) => {
    const attempt = (i) => {
      JSZipUtils.getBinaryContent(url, (err, data) => {
        if (!err) {
          resolve(data);
          return;
        }
        if (i === retries - 1) {
          reject(err);
          return;
        }
        console.warn(
          `[Retry ${i + 1}/${retries}] Retrying JSZipUtils.getBinaryContent for ${url} after ${delays[i]}ms`
        );
        setTimeout(() => attempt(i + 1), delays[i] || 3000);
      });
    };
    attempt(0);
  });
};

function _buildScormZipBlob(state, is2004, selfContained, logPrefix) {
  state = { ...state, filesUploaded: filterUnusedFilesUploaded(state) };
  return new Promise((resolve, reject) => {
    const abortController = new AbortController();
    let isCancelled = false;

    const cancelHandler = () => {
      isCancelled = true;
      abortController.abort();
      console.log(logPrefix + ": Export cancelled by user");
    };
    document.body.addEventListener("canceled", cancelHandler, { once: true });

    const cleanup = () => {
      document.body.removeEventListener("canceled", cancelHandler);
    };

    const checkCancelled = () => {
      if (isCancelled) {
        cleanup();
        reject("Export cancelled");
        return true;
      }
      return false;
    };

    let xhr = new XMLHttpRequest();
    xhr.open("GET", IdealStudio.Config.visor_bundle, true);
    xhr.responseType = "arraybuffer";

    if (checkCancelled()) return;

    try {
      xhr.onreadystatechange = function (evt) {
        if (checkCancelled()) {
          xhr.abort();
          return;
        }

        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            JSZipUtils.getBinaryContent(
              is2004 ? IdealStudio.Config.scorm_zip_2004 : IdealStudio.Config.scorm_zip_12,
              function (err, data) {
                if (err) {
                  console.error(logPrefix + ": Error loading SCORM zip template", err);
                  cleanup();
                  reject(err);
                  return;
                }
                JSZip.loadAsync(data)
                  .then(function (zip) {
                    let navs = state.navItemsById;
                    zip.file(
                      "imsmanifest.xml",
                      IdealStudio.Scorm.createSPAimsManifest(
                        state.exercises,
                        navs,
                        { ...state.globalConfig, status: state.status },
                        is2004
                      )
                    );

                    let page = 0;
                    if (state.navItemsIds && state.navItemsIds.length > 0) {
                      if (!IdealStudio.Config.sections_have_content) {
                        for (let i = 0; i < state.navItemsIds.length; i++) {
                          if (state.navItemsIds[i].indexOf("se-") === -1) {
                            page = state.navItemsIds[i];
                            break;
                          }
                        }
                      } else {
                        page = state.navItemsIds[0];
                      }
                    }
                    state.fromScorm = true;
                    state.navItemSelected = page;

                    const existingUrls = new Set(
                      Object.values(state.filesUploaded || {})
                        .filter((f) => f && f.url)
                        .map((f) => f.url)
                    );
                    const extractedUrls = selfContained
                      ? extractExternalUrlsFromState(state, existingUrls)
                      : new Map();

                    const mergedFilesUploaded = selfContained
                      ? mergeExternalUrls(state.filesUploaded || {}, extractedUrls)
                      : state.filesUploaded;

                    let filteredFiles = selfContained
                      ? filterUserContentFiles(mergedFilesUploaded)
                      : mergedFilesUploaded;
                    const stateForIndex = { ...state };
                    let strState = JSON.stringify({ ...stateForIndex, export: true });
                    let usedNames = [];
                    let filesUploaded = [];
                    if (selfContained) {
                      ({ strState, usedNames, filesUploaded } = localizeFileUrls(
                        strState,
                        filteredFiles
                      ));
                    }
                    let parsedState;
                    try {
                      parsedState = JSON.parse(strState);
                    } catch (parseError) {
                      console.error(logPrefix + ": JSON.parse error", parseError);
                      let fixedStrState = strState.replace(/[\x00-\x1F\x7F]/g, (match) => {
                        if (match === "\n") return "\\n";
                        if (match === "\r") return "\\r";
                        if (match === "\t") return "\\t";
                        return "";
                      });
                      try {
                        parsedState = JSON.parse(fixedStrState);
                      } catch (retryError) {
                        console.error(
                          logPrefix + ": JSON.parse failed even after fixing",
                          retryError
                        );
                        cleanup();
                        reject("Error parsing state: " + parseError.message);
                        return;
                      }
                    }

                    let fontMap = {};
                    let fontFiles = [];

                    // Helper to finalize the zip (add content, bundle, include images, generate blob)
                    const finalizeZip = (zipRef, fontMapRef) => {
                      let content = parseEJS(
                        IdealStudio.Config.visor_ejs,
                        page,
                        {
                          ...parsedState,
                          id: window.idealStudio_editor_params
                            ? window.idealStudio_editor_params.idealStudio_resource_id
                            : null,
                          platform: getPlatform(),
                        },
                        true,
                        selfContained,
                        fontMapRef
                      );
                      zipRef.file("dist/index.html", content);
                      zipRef.file("dist/visor-bundle.js", xhr.response);
                      const zip_title = state.globalConfig.title || "IdealStudio";

                      if (checkCancelled()) return;

                      IdealStudio.Visor.includeImage(
                        zipRef,
                        selfContained ? filesUploaded : [],
                        usedNames,
                        (zipFile, errorReason, skipped) => {
                          if (errorReason === "downloadFailed") {
                            cleanup();
                            reject("downloadFailed");
                            return;
                          }
                          if (!zipFile || checkCancelled()) return;

                          zipFile
                            .generateAsync({ type: "blob" })
                            .then(function (blob) {
                              if (checkCancelled()) return;
                              cleanup();
                              resolve({ blob, title: zip_title, skipped });
                            })
                            .catch((err) => {
                              cleanup();
                              if (err.name === "AbortError" || isCancelled) {
                                reject("Export cancelled");
                              } else {
                                console.error(logPrefix + ": Error generating zip file", err);
                                reject(err || "Error generating zip file");
                              }
                            });
                        },
                        abortController,
                        null,
                        selfContained
                      );
                    };

                    if (selfContained) {
                      const usedFonts = collectTrainingFonts(parsedState);
                      if (usedFonts && usedFonts.size > 0) {
                        fetchGoogleFontsFiles(usedFonts)
                          .then((files) => {
                            fontFiles = files;
                            fontMap = buildExportFontMap(files);

                            // With fonts: include fonts first, then finalize
                            let content = parseEJS(
                              IdealStudio.Config.visor_ejs,
                              page,
                              {
                                ...parsedState,
                                id: window.idealStudio_editor_params
                                  ? window.idealStudio_editor_params.idealStudio_resource_id
                                  : null,
                                platform: getPlatform(),
                              },
                              true,
                              selfContained,
                              fontMap
                            );
                            zip.file("dist/index.html", content);
                            zip.file("dist/visor-bundle.js", xhr.response);
                            const zip_title = state.globalConfig.title || "IdealStudio";

                            if (checkCancelled()) return;

                            IdealStudio.Visor.includeFonts(
                              zip,
                              fontFiles,
                              (zipWithFonts, fontError) => {
                                if (fontError === "downloadFailed") {
                                  cleanup();
                                  reject("downloadFailed");
                                  return;
                                }
                                if (!zipWithFonts || checkCancelled()) return;

                                IdealStudio.Visor.includeImage(
                                  zipWithFonts,
                                  selfContained ? filesUploaded : [],
                                  usedNames,
                                  (zipFile, errorReason, skipped) => {
                                    if (errorReason === "downloadFailed") {
                                      cleanup();
                                      reject("downloadFailed");
                                      return;
                                    }
                                    if (!zipFile || checkCancelled()) return;

                                    zipFile
                                      .generateAsync({ type: "blob" })
                                      .then(function (blob) {
                                        if (checkCancelled()) return;
                                        cleanup();
                                        resolve({ blob, title: zip_title, skipped });
                                      })
                                      .catch((err) => {
                                        cleanup();
                                        if (err.name === "AbortError" || isCancelled) {
                                          reject("Export cancelled");
                                        } else {
                                          console.error(
                                            logPrefix + ": Error generating zip file",
                                            err
                                          );
                                          reject(err || "Error generating zip file");
                                        }
                                      });
                                  },
                                  abortController,
                                  null,
                                  selfContained
                                );
                              },
                              abortController
                            );
                          })
                          .catch((err) => {
                            console.error(logPrefix + ": Error fetching font files", err);
                            finalizeZip(zip, {});
                          });
                        return;
                      }
                    }

                    finalizeZip(zip, fontMap);
                  })
                  .catch((e) => {
                    console.error(logPrefix + ": Error in JSZip.loadAsync", e);
                    cleanup();
                    reject(e);
                  });
              }
            );
          } else {
            console.error(logPrefix + ": XHR failed", xhr.status);
            cleanup();
            reject(xhr.status);
          }
        }
      };
      xhr.send();
    } catch (e) {
      console.error(logPrefix + ": Exception in try block", e);
      cleanup();
      reject(e);
    }
  });
}

// exported for unit tests
export {
  buildExportContentScope,
  extractExternalUrlsFromState,
  filterUnusedFilesUploaded,
  localizeFileUrls,
};

export default {
  Plugins: Plugins(),
  exportsHTML: function (state, callback, selfContained) {
    state = { ...state, filesUploaded: filterUnusedFilesUploaded(state) };
    const abortController = new AbortController();
    let isCancelled = false;

    const cancelHandler = () => {
      isCancelled = true;
      abortController.abort();
      console.log("exportsHTML: Export cancelled by user");
    };
    document.body.addEventListener("canceled", cancelHandler, { once: true });

    let nav_names_used = {};
    let xhr = new XMLHttpRequest();
    let zip_title = state.globalConfig.title || "ideaLStudio";
    xhr.open("GET", IdealStudio.Config.visor_bundle, true);
    xhr.responseType = "arraybuffer";

    if (isCancelled) {
      callback("Export cancelled");
      return;
    }

    try {
      xhr.onreadystatechange = function (evt) {
        if (isCancelled) {
          xhr.abort();
          callback("Export cancelled");
          return;
        }

        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            JSZipUtils.getBinaryContent(IdealStudio.Config.visor_zip, function (err, data) {
              if (err) {
                callback(1);
                throw err; // or handle err
              }
              JSZip.loadAsync(data).then(function (zip) {
                let page = 0;
                if (state.navItemsIds && state.navItemsIds.length > 0) {
                  if (!IdealStudio.Config.sections_have_content) {
                    let i;
                    for (i = 0; i < state.navItemsIds.length; i++) {
                      if (state.navItemsIds[i].indexOf("se-") === -1) {
                        page = state.navItemsIds[i];
                        break;
                      }
                    }
                  } else {
                    page = state.navItemsIds[0];
                  }
                }
                state.navItemSelected = page;

                const existingUrls = new Set(
                  Object.values(state.filesUploaded || {})
                    .filter((f) => f && f.url)
                    .map((f) => f.url)
                );
                const extractedUrls = selfContained
                  ? extractExternalUrlsFromState(state, existingUrls)
                  : new Map();

                const mergedFilesUploaded = selfContained
                  ? mergeExternalUrls(state.filesUploaded || {}, extractedUrls)
                  : state.filesUploaded;

                // Filter to exclude only system files when selfContained is true
                let filteredFiles = selfContained
                  ? filterUserContentFiles(mergedFilesUploaded)
                  : mergedFilesUploaded;
                const stateForIndex = { ...state, export: true };
                let strState = JSON.stringify(stateForIndex);
                let usedNames = [];
                let filesUploaded = [];
                if (selfContained) {
                  ({ strState, usedNames, filesUploaded } = localizeFileUrls(
                    strState,
                    filteredFiles
                  ));
                }

                let parsedState;
                try {
                  parsedState = JSON.parse(strState);
                } catch (parseError) {
                  console.error("exportsHTML: JSON.parse error", parseError);
                  // Try to fix common JSON issues
                  let fixedStrState = strState.replace(/[\x00-\x1F\x7F]/g, (match) => {
                    if (match === "\n") return "\\n";
                    if (match === "\r") return "\\r";
                    if (match === "\t") return "\\t";
                    return "";
                  });
                  try {
                    parsedState = JSON.parse(fixedStrState);
                  } catch (retryError) {
                    console.error("exportsHTML: JSON.parse failed even after fixing", retryError);
                    callback("Error parsing state: " + parseError.message);
                    return;
                  }
                }
                let fontMap = {};
                let fontFiles = [];

                if (selfContained) {
                  const usedFonts = collectTrainingFonts(parsedState);
                  if (usedFonts && usedFonts.size > 0) {
                    fetchGoogleFontsFiles(usedFonts)
                      .then((files) => {
                        fontFiles = files;

                        fontMap = buildExportFontMap(files);

                        let content = parseEJS(
                          IdealStudio.Config.visor_ejs,
                          page,
                          {
                            ...parsedState,
                            id: window.idealStudio_editor_params
                              ? window.idealStudio_editor_params.idealStudio_resource_id
                              : null,
                            platform: getPlatform(),
                          },
                          false,
                          selfContained,
                          fontMap
                        );

                        zip.file("dist/index.html", content);
                        zip.file("dist/visor-bundle.js", xhr.response);

                        if (isCancelled) {
                          document.body.removeEventListener("canceled", cancelHandler);
                          callback("Export cancelled");
                          return;
                        }

                        IdealStudio.Visor.includeFonts(
                          zip,
                          fontFiles,
                          (zipWithFonts, fontError) => {
                            if (fontError === "downloadFailed") {
                              document.body.removeEventListener("canceled", cancelHandler);
                              callback("downloadFailed");
                              return;
                            }
                            if (!zipWithFonts) {
                              document.body.removeEventListener("canceled", cancelHandler);
                              callback("Export cancelled");
                              return;
                            }

                            if (isCancelled) {
                              document.body.removeEventListener("canceled", cancelHandler);
                              callback("Export cancelled");
                              return;
                            }

                            IdealStudio.Visor.includeImage(
                              zipWithFonts,
                              selfContained ? filesUploaded : [],
                              usedNames,
                              (zipFile, errorReason) => {
                                if (errorReason === "downloadFailed") {
                                  document.body.removeEventListener("canceled", cancelHandler);
                                  callback("downloadFailed");
                                  return;
                                }
                                if (!zipFile) {
                                  document.body.removeEventListener("canceled", cancelHandler);
                                  callback("Export cancelled");
                                  return;
                                }

                                if (isCancelled) {
                                  document.body.removeEventListener("canceled", cancelHandler);
                                  callback("Export cancelled");
                                  return;
                                }

                                zipFile
                                  .generateAsync({ type: "blob" })
                                  .then(function (blob) {
                                    if (isCancelled) {
                                      document.body.removeEventListener("canceled", cancelHandler);
                                      callback("Export cancelled");
                                      return;
                                    }
                                    saveAs(
                                      blob,
                                      zip_title.toLowerCase().replace(/\s/g, "") +
                                        Math.round(+new Date() / 1000) +
                                        "_HTML.zip"
                                    );
                                    document.body.removeEventListener("canceled", cancelHandler);
                                    callback();
                                  })
                                  .catch((err) => {
                                    document.body.removeEventListener("canceled", cancelHandler);
                                    if (err.name === "AbortError" || isCancelled) {
                                      callback("Export cancelled");
                                    } else {
                                      console.error("exportsHTML: Error generating zip file", err);
                                      callback(err || "Error generating zip file");
                                    }
                                  });
                              },
                              abortController,
                              null,
                              selfContained
                            );
                          },
                          abortController
                        );
                      })
                      .catch((err) => {
                        console.error("exportsHTML: Error fetching font files", err);

                        let content = parseEJS(
                          IdealStudio.Config.visor_ejs,
                          page,
                          {
                            ...parsedState,
                            id: window.idealStudio_editor_params
                              ? window.idealStudio_editor_params.idealStudio_resource_id
                              : null,
                            platform: getPlatform(),
                          },
                          false,
                          selfContained,
                          {}
                        );

                        zip.file("dist/index.html", content);
                        zip.file("dist/visor-bundle.js", xhr.response);

                        if (isCancelled) {
                          document.body.removeEventListener("canceled", cancelHandler);
                          callback("Export cancelled");
                          return;
                        }

                        IdealStudio.Visor.includeImage(
                          zip,
                          selfContained ? filesUploaded : [],
                          usedNames,
                          (zipFile, errorReason) => {
                            if (errorReason === "downloadFailed") {
                              document.body.removeEventListener("canceled", cancelHandler);
                              callback("downloadFailed");
                              return;
                            }
                            if (!zipFile) {
                              document.body.removeEventListener("canceled", cancelHandler);
                              callback("Export cancelled");
                              return;
                            }

                            if (isCancelled) {
                              document.body.removeEventListener("canceled", cancelHandler);
                              callback("Export cancelled");
                              return;
                            }

                            zipFile
                              .generateAsync({ type: "blob" })
                              .then(function (blob) {
                                if (isCancelled) {
                                  document.body.removeEventListener("canceled", cancelHandler);
                                  callback("Export cancelled");
                                  return;
                                }
                                saveAs(
                                  blob,
                                  zip_title.toLowerCase().replace(/\s/g, "") +
                                    Math.round(+new Date() / 1000) +
                                    "_HTML.zip"
                                );
                                document.body.removeEventListener("canceled", cancelHandler);
                                callback();
                              })
                              .catch((err) => {
                                document.body.removeEventListener("canceled", cancelHandler);
                                if (err.name === "AbortError" || isCancelled) {
                                  callback("Export cancelled");
                                } else {
                                  console.error("exportsHTML: Error generating zip file", err);
                                  callback(err || "Error generating zip file");
                                }
                              });
                          },
                          abortController,
                          null,
                          selfContained
                        );
                      });
                    return;
                  }
                }

                let content = parseEJS(
                  IdealStudio.Config.visor_ejs,
                  page,
                  {
                    ...parsedState,
                    id: window.idealStudio_editor_params
                      ? window.idealStudio_editor_params.idealStudio_resource_id
                      : null,
                    platform: getPlatform(),
                  },
                  false,
                  selfContained,
                  fontMap
                );

                zip.file("dist/index.html", content);
                zip.file("dist/visor-bundle.js", xhr.response);
                // zip.file("idealStudio.edi", strState);
                if (isCancelled) {
                  document.body.removeEventListener("canceled", cancelHandler);
                  callback("Export cancelled");
                  return;
                }

                IdealStudio.Visor.includeImage(
                  zip,
                  selfContained ? filesUploaded : [],
                  usedNames,
                  (zipFile, errorReason) => {
                    if (errorReason === "downloadFailed") {
                      document.body.removeEventListener("canceled", cancelHandler);
                      callback("downloadFailed");
                      return;
                    }
                    if (!zipFile) {
                      document.body.removeEventListener("canceled", cancelHandler);
                      callback("Export cancelled");
                      return;
                    }

                    if (isCancelled) {
                      document.body.removeEventListener("canceled", cancelHandler);
                      callback("Export cancelled");
                      return;
                    }

                    zipFile
                      .generateAsync({ type: "blob" })
                      .then(function (blob) {
                        if (isCancelled) {
                          document.body.removeEventListener("canceled", cancelHandler);
                          callback("Export cancelled");
                          return;
                        }
                        // FileSaver.saveAs(blob, "idealStudiovisor.zip");
                        saveAs(
                          blob,
                          zip_title.toLowerCase().replace(/\s/g, "") +
                            Math.round(+new Date() / 1000) +
                            "_HTML.zip"
                        );
                        document.body.removeEventListener("canceled", cancelHandler);
                        callback();
                      })
                      .catch((err) => {
                        document.body.removeEventListener("canceled", cancelHandler);
                        if (err.name === "AbortError" || isCancelled) {
                          callback("Export cancelled");
                        } else {
                          console.error("exportsHTML: Error generating zip file", err);
                          callback(err || "Error generating zip file");
                        }
                      });
                  },
                  abortController,
                  null,
                  selfContained
                );
              });
            });
          } else {
            document.body.removeEventListener("canceled", cancelHandler);
            callback("error");
          }
        }
      };

      xhr.onabort = () => {
        document.body.removeEventListener("canceled", cancelHandler);
        callback("Export cancelled");
      };

      xhr.send();
    } catch (e) {
      document.body.removeEventListener("canceled", cancelHandler);
      callback(e);
    }
  },
  exportsURL: function (state, callback, selfContained, trainingId, version) {
    // For URL export, we need to filter filesUploaded to keep external URLs in content
    let cleanedFilesUploaded = {};

    // Only keep base64-embedded files (they're already in the state and don't need separate handling)
    // External URLs (http/https) should remain in the content but not in filesUploaded
    if (state.filesUploaded) {
      for (let key in state.filesUploaded) {
        let file = state.filesUploaded[key];
        // Keep only base64 files in filesUploaded for URL export
        if (file && file.url && file.url.includes("base64")) {
          cleanedFilesUploaded[key] = file;
        }
        // External URLs will remain in the content as-is, but won't be in filesUploaded
        // This prevents the server from trying to map them to local paths like images/uploadedFiles/
      }
    }

    let cleanedState = {
      ...state,
      filesUploaded: cleanedFilesUploaded,
      export: true,
    };

    let strState = JSON.stringify(cleanedState);
    _LSCreateUrlHtml(trainingId, strState, callback);

    // let nav_names_used = {};
    // let xhr = new XMLHttpRequest();
    // let zip_title = state.globalConfig.title || "ideaLStudio";
    // xhr.open('GET', IdealStudio.Config.visor_bundle, true);
    // xhr.responseType = "arraybuffer";
    // try{
    //     xhr.onreadystatechange = function(evt) {
    //         if (xhr.readyState === 4) {
    //             if (xhr.status === 200) {

    //                 JSZipUtils.getBinaryContent(IdealStudio.Config.visor_zip, function(err, data) {
    //                     if (err) {
    //                         callback(1);
    //                         throw err; // or handle err

    //                     }
    //                     JSZip.loadAsync(data).then(function(zip) {

    //                         let page = 0;
    //                         if (state.navItemsIds && state.navItemsIds.length > 0) {
    //                             if(!IdealStudio.Config.sections_have_content) {
    //                                 let i;
    //                                 for (i = 0; i < state.navItemsIds.length; i++) {
    //                                     if (state.navItemsIds[i].indexOf('se-') === -1) {
    //                                         page = state.navItemsIds[i];
    //                                         break;
    //                                     }
    //                                 }
    //                             } else {
    //                                 page = state.navItemsIds[0];
    //                             }
    //                         }
    //                         state.navItemSelected = page;
    //                         let filesUploaded = Object.values(state.filesUploaded);
    //                         let strState = JSON.stringify({ ...state, export: true });
    //                         let usedNames = [];
    //                         if (selfContained) {
    //                             let index = 0;
    //                             for (let f in state.filesUploaded) {

    //                                 let file = state.filesUploaded[f];
    //                                 let r = new RegExp(escapeRegExp(file.url), "g");
    //                                 let name = "idealstudio_" + index + "_" + file.name;
    //                                 if (usedNames.indexOf(name) > -1) {
    //                                     name = Date.now() + '_' + name;
    //                                 }
    //                                 usedNames.push(name);

    //                                 strState = strState.replace(r, 'images/uploadedFiles/' + name);
    //                                 index++;
    //                             }
    //                         }

    //                         let content = parseEJS(IdealStudio.Config.visor_ejs, page, { ...JSON.parse(strState), id: window.idealStudio_editor_params ? window.idealStudio_editor_params.idealStudio_resource_id : null, platform: getPlatform() }, false, false);

    //                         zip.file("index.html", content);
    //                         zip.file("visor-bundle.js", xhr.response);
    //                         // zip.file("idealStudio.edi", strState);
    //                         IdealStudio.Visor.includeImage(zip, selfContained ? filesUploaded : [], usedNames, (zipFile) => {

    //                             zipFile.generateAsync({ type: "blob" }).then(function(blob) {
    //                                 // FileSaver.saveAs(blob, zip_title.toLowerCase().replace(/\s/g, '') + Math.round(+new Date() / 1000) + "_HTML.zip");
    //                                 _LSUploadHtml(trainingId, version, new File([blob], zip_title.toLowerCase().replace(/\s/g, '') + Math.round(+new Date() / 1000) + "_HTML.zip", { type: "application/zip" }), callback);

    //                             });
    //                         });
    //                     });
    //                 });

    //             } else {
    //                 callback('error');
    //             }
    //         }
    //     };
    //     xhr.send();
    // } catch (e) {
    //     callback(e);
    // }
  },
  includeImage(
    zip,
    filesUploaded,
    usedNames,
    callback,
    abortController = null,
    maxConcurrent = null,
    selfContained = false,
    skipped = []
  ) {
    if (maxConcurrent === null) {
      const totalFiles = filesUploaded.length;
      if (totalFiles > 100) {
        maxConcurrent = 15;
      } else if (totalFiles > 50) {
        maxConcurrent = 10;
      } else if (totalFiles > 20) {
        maxConcurrent = 8;
      } else {
        maxConcurrent = 5;
      }
    }

    if (abortController && abortController.signal.aborted) {
      console.log("includeImage: Export cancelled, aborting file downloads");
      callback(null);
      return;
    }

    if (filesUploaded.length === 0) {
      callback(zip, undefined, skipped);
      return;
    }

    const batchSize = Math.min(maxConcurrent, filesUploaded.length);
    const batch = [];
    const batchNames = [];

    for (let i = 0; i < batchSize && filesUploaded.length > 0; i++) {
      batch.push(filesUploaded.shift());
      batchNames.push(usedNames.shift());
    }

    // An unreachable image (e.g. a deleted brand logo) is skipped and reported;
    // anything else (audio, video, pdf, html) still fails the package.
    const skipOrFail = (file, name, err) => {
      if (!/\.(jpe?g|png|gif|svg|webp|bmp|ico)$/i.test(name)) throw new Error("downloadFailed");
      console.warn("includeImage: skipping unreachable image", file.url, err);
      skipped.push(file.url);
      return { success: true, name, skipped: true };
    };

    const promises = batch.map((file, index) => {
      const name = batchNames[index];

      // Validate file before processing
      if (!file || !file.url || typeof file.url !== "string") {
        return Promise.resolve(null);
      }

      // Skip PDF files when not selfContained - they will be loaded from URL directly
      const isPdfFile = name.toLowerCase().endsWith(".pdf");
      if (isPdfFile && !selfContained) {
        return Promise.resolve({ success: true, name, skipped: true });
      }

      // Handle external URLs (http/https) - download and include in zip
      if (file.url.startsWith("http://") || file.url.startsWith("https://")) {
        if (abortController && abortController.signal.aborted) {
          return Promise.resolve(null);
        }

        return fetchWithRetry(file.url, {
          ...credentialsFor(file.url),
          signal: abortController ? abortController.signal : undefined,
        })
          .then((response) => {
            if (abortController && abortController.signal.aborted) {
              throw new Error("Export cancelled");
            }
            // we don't throw HTTP errors here since fetchWithRetry already handles that
            return response.blob();
          })
          .then((blob) => {
            if (abortController && abortController.signal.aborted) {
              throw new Error("Export cancelled");
            }
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                if (abortController && abortController.signal.aborted) {
                  reject(new Error("Export cancelled"));
                } else {
                  resolve(reader.result);
                }
              };
              reader.onerror = reject;
              reader.readAsArrayBuffer(blob);
            });
          })
          .then((data) => {
            if (abortController && abortController.signal.aborted) {
              throw new Error("Export cancelled");
            }
            // All files go to dist/images/uploadedFiles/ for both SCORM and HTML exports
            // PDF files go to dist/images/ to avoid CORS issues with worker
            const isPdf = name.toLowerCase().endsWith(".pdf");
            const imagePath = isPdf ? "dist/images/" + name : "dist/images/uploadedFiles/" + name;
            zip.file(imagePath, data, { binary: true });

            // For PDFs, create a JS wrapper with Base64 data to bypass CORS in offline mode
            if (isPdf) {
              try {
                let binary = "";
                const bytes = new Uint8Array(data);
                const len = bytes.byteLength;
                for (let i = 0; i < len; i++) {
                  binary += String.fromCharCode(bytes[i]);
                }
                const base64 = window.btoa(binary);
                const jsContent = `window.pdfData = window.pdfData || {}; window.pdfData["${name}"] = "${base64}";`;
                zip.file(imagePath + ".js", jsContent);
              } catch (e) {
                console.error("Error creating PDF JS wrapper for external URL", e);
              }
            }

            return { success: true, name };
          })
          .catch((err) => {
            if (err.name === "AbortError" || (err.message && err.message === "Export cancelled")) {
              throw err;
            }
            console.error(
              "includeImage: Error downloading external URL after 3 attempts",
              file.url,
              err
            );
            return skipOrFail(file, name, err);
          });
      }

      return getBinaryContentWithRetry(file.url)
        .then((data) => {
          if (abortController && abortController.signal.aborted) {
            throw new Error("Export cancelled");
          }

          // All files go to dist/images/uploadedFiles/ for both SCORM and HTML exports
          // PDF files go to dist/images/ and are also wrapped in JS to avoid CORS issues
          const isPdf = name.toLowerCase().endsWith(".pdf");
          const imagePath = isPdf ? "dist/images/" + name : "dist/images/uploadedFiles/" + name;

          // Always save the binary file
          zip.file(imagePath, data, { binary: true });

          // For PDFs, create a JS wrapper with Base64 data
          if (isPdf) {
            try {
              let binary = "";
              const bytes = new Uint8Array(data);
              const len = bytes.byteLength;
              for (let i = 0; i < len; i++) {
                binary += String.fromCharCode(bytes[i]);
              }
              const base64 = window.btoa(binary);
              const jsContent = `window.pdfData = window.pdfData || {}; window.pdfData["${name}"] = "${base64}";`;
              zip.file(imagePath + ".js", jsContent);
            } catch (e) {
              console.error("Error creating PDF JS wrapper", e);
            }
          }

          return { success: true, name };
        })
        .catch((err) => {
          if (err.name === "AbortError" || (err.message && err.message === "Export cancelled")) {
            throw err;
          }
          console.error("includeImage: Error loading file after 3 attempts", file.url, err);
          return skipOrFail(file, name, err);
        });
    });

    Promise.all(promises)
      .then(() => {
        IdealStudio.Visor.includeImage(
          zip,
          filesUploaded,
          usedNames,
          callback,
          abortController,
          maxConcurrent,
          selfContained,
          skipped
        );
      })
      .catch((err) => {
        if (err.name === "AbortError" || (err.message && err.message === "Export cancelled")) {
          console.log("includeImage: Export cancelled during batch processing");
          callback(null, "Export cancelled");
        } else if (err.message === "downloadFailed") {
          console.log("includeImage: Download failed for one or more files");
          callback(null, "downloadFailed");
        } else {
          console.log("includeImage: Unexpected error during batch processing", err);
          callback(null, "downloadFailed");
        }
      });
  },
  includeFonts: function (zip, fontFiles, callback, abortController = null) {
    if (!fontFiles || fontFiles.length === 0) {
      callback(zip);
      return;
    }

    if (abortController && abortController.signal.aborted) {
      console.log("includeFonts: Export cancelled, aborting font downloads");
      callback(null);
      return;
    }

    // Maksimum font dosyası boyutu (5MB)
    const MAX_FONT_FILE_SIZE = 5 * 1024 * 1024;

    // Font dosyalarını indir ve ZIP'e ekle
    const promises = fontFiles.map((file) => {
      if (abortController && abortController.signal.aborted) {
        return Promise.resolve(null);
      }

      // Güvenlik kontrolü: URL validasyonu
      if (!file.url || typeof file.url !== "string") {
        console.warn("includeFonts: Geçersiz font URL:", file);
        return Promise.resolve({ success: false, error: "Invalid URL" });
      }

      // Güvenlik kontrolü: Sadece HTTPS ve Google Fonts domain'lerine izin ver
      try {
        const urlObj = new URL(file.url);
        const allowedDomains = ["fonts.googleapis.com", "fonts.gstatic.com"];
        const isValidDomain = allowedDomains.some(
          (domain) => urlObj.hostname === domain || urlObj.hostname.endsWith("." + domain)
        );

        if (!isValidDomain || urlObj.protocol !== "https:") {
          console.warn("includeFonts: Güvenlik: Geçersiz font URL domain:", file.url);
          return Promise.resolve({ success: false, error: "Invalid domain" });
        }
      } catch (error) {
        console.warn("includeFonts: Geçersiz URL formatı:", file.url, error);
        return Promise.resolve({ success: false, error: "Invalid URL format" });
      }

      // Güvenlik kontrolü: Dosya uzantısı
      const validExtensions = [".woff2", ".woff", ".ttf", ".otf"];
      const fileExtension = file.fileName
        ? file.fileName.toLowerCase().substring(file.fileName.lastIndexOf("."))
        : "";
      if (!validExtensions.includes(fileExtension)) {
        console.warn("includeFonts: Güvenlik: Geçersiz font dosya uzantısı:", file.fileName);
        return Promise.resolve({ success: false, error: "Invalid file extension" });
      }

      const sanitizedFileName = file.fileName.replace(/\.\./g, "").replace(/[^a-zA-Z0-9._-]/g, "_");

      return fetchWithRetry(file.url, {
        ...credentialsFor(file.url),
        signal: abortController ? abortController.signal : undefined,
      })
        .then((response) => {
          if (abortController && abortController.signal.aborted) {
            throw new Error("Export cancelled");
          }
          const contentLength = response.headers.get("content-length");
          if (contentLength && parseInt(contentLength) > MAX_FONT_FILE_SIZE) {
            throw new Error(`Font file too large: ${contentLength} bytes`);
          }

          return response.blob();
        })
        .then((blob) => {
          if (abortController && abortController.signal.aborted) {
            throw new Error("Export cancelled");
          }

          // Dosya boyutu kontrolü
          if (blob.size > MAX_FONT_FILE_SIZE) {
            throw new Error(`Font file too large: ${blob.size} bytes`);
          }

          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              if (abortController && abortController.signal.aborted) {
                reject(new Error("Export cancelled"));
              } else {
                resolve(reader.result);
              }
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(blob);
          });
        })
        .then((data) => {
          if (abortController && abortController.signal.aborted) {
            throw new Error("Export cancelled");
          }

          // Font dosyasını ZIP'e ekle (dist/fonts/google/ klasörüne)
          const fontPath = "dist/fonts/google/" + sanitizedFileName;
          zip.file(fontPath, data, { binary: true });
          return { success: true, fileName: sanitizedFileName };
        })
        .catch((err) => {
          if (err.name === "AbortError" || (err.message && err.message === "Export cancelled")) {
            throw err;
          }
          console.error(
            "includeFonts: Error downloading font file after 3 attempts",
            file.url,
            err
          );
          throw new Error("downloadFailed");
        });
    });

    Promise.all(promises)
      .then((results) => {
        if (abortController && abortController.signal.aborted) {
          callback(null, "Export cancelled");
          return;
        }

        callback(zip, null);
      })
      .catch((err) => {
        if (err.name === "AbortError" || (err.message && err.message === "Export cancelled")) {
          console.log("includeFonts: Export cancelled during batch processing");
          callback(null, "Export cancelled");
        } else if (err.message === "downloadFailed") {
          console.log("includeFonts: Download failed for one or more fonts");
          callback(null, "downloadFailed");
        } else {
          console.error("includeFonts: Unexpected error during batch processing", err);
          callback(null, "downloadFailed");
        }
      });
  },
  exportPage: function (state) {
    // Ensure we have a valid initial page selected for preview
    if (!state.navItemSelected || state.navItemSelected === 0) {
      // Find the first valid page to display
      if (state.navItemsIds && state.navItemsIds.length > 0) {
        if (!IdealStudio.Config.sections_have_content) {
          // Find the first non-section page
          for (let i = 0; i < state.navItemsIds.length; i++) {
            if (state.navItemsIds[i].indexOf("se-") === -1) {
              state.navItemSelected = state.navItemsIds[i];
              break;
            }
          }
        } else {
          // Use the first item in the list
          state.navItemSelected = state.navItemsIds[0];
        }
      }
    }

    // Preview'da sadece Google Fonts link'i kullan, font dosyaları yok
    const usedFonts = collectTrainingFonts(state);
    const googleFontsLink = generateGoogleFontsLink(usedFonts);
    // Preview'da dynamicFontFaceCSS kullanma
    const dynamicFontFaceCSS = "";

    if (
      state.navItemSelected &&
      state.navItemsById[state.navItemSelected] &&
      Object.keys(state.navItemsById[state.navItemSelected].extraFiles).length !== 0
    ) {
      let extraFileBox = Object.keys(state.navItemsById[state.navItemSelected].extraFiles)[0];
      let extraFileContainer = state.pluginToolbarsById[extraFileBox];
      state.fromScorm = false;
      return visor_template({
        visor_bundle_path: IdealStudio.Config.visor_bundle,
        state: state,
        reason: "preview",
        // Dev bundles run on eval (eval-cheap-module-source-map); the export CSP blocks that.
        cspAllowEval: process.env.NODE_ENV === "development",
        googleFontsLink: googleFontsLink,
        dynamicFontFaceCSS: dynamicFontFaceCSS,
      });
    }

    state.fromScorm = false;
    return visor_template({
      state: state,
      visor_bundle_path: IdealStudio.Config.visor_bundle,
      fromScorm: false,
      reason: "preview",
      // Dev bundles run on eval (eval-cheap-module-source-map); the export CSP blocks that.
      cspAllowEval: process.env.NODE_ENV === "development",
      googleFontsLink: googleFontsLink,
      dynamicFontFaceCSS: dynamicFontFaceCSS,
    });
  },
  exportScorm: function (state, is2004, callback, selfContained) {
    _buildScormZipBlob(state, is2004, selfContained, "exportScorm")
      .then(({ blob, title, skipped }) => {
        saveAs(
          blob,
          title.toLowerCase().replace(/\s/g, "") +
            Math.round(+new Date() / 1000) +
            (is2004 ? "_2004" : "_1.2") +
            ".zip"
        );
        callback(undefined, skipped);
      })
      .catch((err) => {
        callback(err);
      });
  },
  exportScenesScorm: async function (state, selfContained, onProgress, callback, onlyId) {
    // onlyId: export just that scene as one plain SCORM zip (no wrapper zip).
    const ids = onlyId ? sceneIds(state).filter((id) => id === onlyId) : sceneIds(state);
    if (onlyId && !ids.length) return callback("Scene not found");
    const outer = new JSZip();
    const failed = [];
    const skipped = [];
    let cancelled = false;
    const onCancel = () => (cancelled = true);
    document.body.addEventListener("canceled", onCancel, { once: true });
    try {
      for (let i = 0; i < ids.length && !cancelled; i++) {
        onProgress(i + 1, ids.length);
        const name = sceneName(state, ids[i]);
        try {
          // sequential on purpose: one package in memory at a time
          const { blob, skipped: sceneSkipped } = await _buildScormZipBlob(
            buildSceneState(state, ids[i]),
            true,
            selfContained,
            "exportScenesScorm"
          );
          const slug = name
            .toLowerCase()
            .normalize("NFKD")
            .replace(/ı/g, "i")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "");
          if (onlyId) {
            saveAs(
              blob,
              (state.globalConfig.title || "IdealStudio").toLowerCase().replace(/\s/g, "") +
                "-" +
                (sceneIds(state).indexOf(onlyId) + 1) +
                "-" +
                (slug || "scene") +
                "-scorm.zip"
            );
            return callback(null, [], sceneSkipped);
          }
          outer.file(String(i + 1).padStart(2, "0") + "-" + (slug || "scene") + ".zip", blob);
          sceneSkipped.forEach((u) => skipped.includes(u) || skipped.push(u));
        } catch (err) {
          if (cancelled) break;
          if (onlyId) throw err;
          console.error("exportScenesScorm: scene failed", ids[i], err);
          failed.push(i + 1 + " " + name);
        }
      }
      if (cancelled) return callback("Export cancelled");
      const blob = await outer.generateAsync({ type: "blob" });
      saveAs(
        blob,
        (state.globalConfig.title || "IdealStudio").toLowerCase().replace(/\s/g, "") +
          "-scenes-scorm.zip"
      );
      callback(null, failed, skipped);
    } catch (err) {
      callback(err);
    } finally {
      document.body.removeEventListener("canceled", onCancel);
    }
  },
  exportToLMS: function (state, callback, selfContained) {
    selfContained = selfContained !== undefined ? selfContained : true;
    const is2004 = true; //TODO: Change to dynamic selection

    _buildScormZipBlob(state, is2004, selfContained, "exportToLMS")
      .then(({ blob, title }) => {
        callback(null, blob, title);
      })
      .catch((err) => {
        callback(err);
      });
  },
  exportsEDI: function (state, callback) {
    let page = 0;
    if (state.navItemsIds && state.navItemsIds.length > 0) {
      if (!IdealStudio.Config.sections_have_content) {
        let i;
        for (i = 0; i < state.navItemsIds.length; i++) {
          if (state.navItemsIds[i].indexOf("se-") === -1) {
            page = state.navItemsIds[i];
            break;
          }
        }
      } else {
        page = state.navItemsIds[0];
      }
    }
    state.navItemSelected = page;
    let filesUploaded = Object.values(state.filesUploaded);
    let strState = JSON.stringify({ ...state, export: true });
    let usedNames = [];
    let content = parseEJS(IdealStudio.Config.visor_ejs, page, JSON.parse(strState), false);
    window.download(strState, "idealStudio.edi", "text/json");
    callback();
  },
  exitLSEditorPage: function (state, callback, selfContained, trainingId, version) {
    if (window.isPublicPreviewMode === true) {
      return;
    }
    _LSExitAndReturnList();
  },
};

function getPlatform() {
  let allowedDomains = ["localhost:3000", "localhost:8080", "https://www.ideaelearning.net/"];
  let allowedDomain = false;
  allowedDomains.map((domain, i) => {
    if (window.location.href.indexOf(domain) > -1) {
      allowedDomain = domain;
    }
  });
  return allowedDomain;
}
